import { computed, defineComponent, Fragment, h, reactive, shallowRef, toRef, toRefs, unref, watchEffect } from 'vue';

import {
  makeDisabledProps,
  makeLoadingProps,
  makeLocaleProps,
  makeReadonlyProps,
  makeSizeProps,
  useRender
} from '@components/composable';
import { warn, reactivePick, classNames, pickAttrs } from '@components/utils';

import { makeTableProps, useParseColumns, useScroll } from '../../composable';
import { INTERNAL_HOOKS } from '../../constants';
import { useProvideBody, useProvideExpandedRow, useProvideResize, useProvideTable } from '../../context';
import { getPathValue, mergeObject, findAllChildrenKeys, getCellFixedInfo } from '../../utils';
import { Body } from '../Body';
import { ColGroup } from '../ColGroup';
import { FixedHolder } from '../FixedHolder';
import { Footer } from '../Footer';
import { Header } from '../Header';
import { renderExpandIcon } from '../Icons/ExpandIcon';
import { Panel } from '../Panel';
import { ResizeObserver } from '../ResizeObserver';
import { StickyScrollbar } from '../StickyScrollbar';

import type { ITableProps } from '../../composable';
import type { NTable } from '../../interface';
import type { IFixedHolderProps } from '../FixedHolder/FixedHolder';
import type { PropType, VNode, CSSProperties } from 'vue';

// Used for conditions cache
const EMPTY_DATA: NTable.TDefaultRecord = [];

const componentName = 'InternalTable';
export default defineComponent({
  name: componentName,
  inheritAttrs: false,
  props: {
    ...makeTableProps(),
    ...makeSizeProps(),
    ...makeLocaleProps(),
    ...makeLoadingProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),

    // Internal props
    internalHooks: [String],
    transformColumns: {
      type: Function as PropType<ITableProps['transformColumns']>
    },
    internalRefs: {
      type: Object as PropType<ITableProps['internalRefs']>
    },
    onUpdateInternalRefs: {
      type: Function as PropType<ITableProps['onUpdateInternalRefs']>
    }
  },
  emits: ['change', 'expand', 'expandedRowsChange', 'updateInternalRefs', 'update:expandedRowKeys'],
  setup(props, { attrs, slots, emit }) {
    const mergedData = computed(() => props.data || EMPTY_DATA);
    const hasData = computed(() => !!mergedData.value.length);

    // ==================== Customize =====================
    const mergedComponents = computed(() =>
      mergeObject<NTable.NCustomize.TableComponents<any>>(
        props.components as NTable.NCustomize.TableComponents<any>,
        {}
      )
    );

    const getComponent = (path: NTable.TDataIndex, defaultComponent?: string) =>
      getPathValue<NTable.NCustomize.TComponent, NTable.NCustomize.TableComponents<any>>(
        mergedComponents.value,
        path
      ) || defaultComponent;

    const getRowKey = computed(() => {
      const rowKey = props.rowKey;

      if (typeof rowKey === 'function') {
        return rowKey;
      }

      return (record: NTable.TDefaultRecord) => {
        const key = record & record[rowKey!];

        if (key === undefined) {
          warn(
            'CdsTable',
            'Each record in table should have a unique `key` prop, or set `rowKey` to an unique primary key.'
          );
        }

        return key;
      };
    });
    // ==================== Customize =====================

    // ====================== Expand ======================
    const mergedExpandIcon = computed(
      () => props.expandIcon || renderExpandIcon(props.locale as NTable.ILocale, props.size)
    );
    const mergedChildrenColumnName = computed(() => props.childrenColumnName || 'children');
    const expandableType = computed(() => {
      if (props.expandedRowRender) return 'row';

      if (
        props.canExpandable ||
        mergedData.value.some(
          (record: NTable.TDefaultRecord) =>
            record && typeof record === 'object' && record[mergedChildrenColumnName.value]
        )
      ) {
        return 'nest';
      }

      return false;
    });

    const innerExpandedKeys = shallowRef<NTable.TKey[] | undefined>([]);

    const stop = watchEffect(() => {
      if (props.defaultExpandAllRows) {
        innerExpandedKeys.value = props.defaultExpandedRowKeys;
      }

      if (props.defaultExpandAllRows) {
        innerExpandedKeys.value = findAllChildrenKeys(
          mergedData.value,
          getRowKey.value,
          mergedChildrenColumnName.value
        );
      }
    });

    stop();

    const mergedExpandedKeys = computed(() => new Set(props.expandedRowKeys || innerExpandedKeys.value || []));

    const onTriggerExpand: NTable.TTriggerEventHandler<NTable.TDefaultRecord> = (record) => {
      const key = getRowKey.value(record, mergedData.value.indexOf(record));

      let newExpandedKeys: NTable.TKey[];
      const hasKey = mergedExpandedKeys.value.has(key);

      if (hasKey) {
        mergedExpandedKeys.value.delete(key);
        newExpandedKeys = [...mergedExpandedKeys.value];
      } else {
        newExpandedKeys = [...mergedExpandedKeys.value, key];
      }

      innerExpandedKeys.value = newExpandedKeys;

      emit('expand', !hasKey, record);
      emit('update:expandedRowKeys', newExpandedKeys);
      emit('expandedRowsChange', newExpandedKeys);
    };

    // Warning if use `expandedRowRender` and nest children in the same time
    if (
      props.expandedRowRender &&
      mergedData.value.some((record: NTable.TDefaultRecord) => Array.isArray(record?.[mergedChildrenColumnName.value]))
    ) {
      warn('CdsTable', '`expandedRowRender` should not use with nested Table');
    }

    const { columns, flattenColumns } = useParseColumns(
      {
        columns: toRef(props, 'columns', []),
        rowExpandable: toRef(props, 'rowExpandable'),
        expandIcon: mergedExpandIcon as any,
        expandRowByClick: toRef(props, 'expandRowByClick'),
        expandColumnWidth: toRef(props, 'expandColumnWidth'),
        expandFixed: toRef(props, 'expandFixed'),
        expandable: computed(() => !!props.expandedRowRender),
        expandedKeys: mergedExpandedKeys,
        getRowKey,
        onTriggerExpand
      },
      computed(() => (props.internalHooks === INTERNAL_HOOKS ? props.transformColumns : null)) as any
    );

    const columnContext = computed(() => ({
      columns: columns.value,
      flattenColumns: flattenColumns.value
    }));
    // ====================== Expand ======================

    // ====================== Scroll ======================
    const {
      fixHeader,
      fixColumn,
      fixFooter,
      horizonScroll,
      stickyOffsets,
      stickyState,
      scrollbarSize,
      mergedTableLayout,
      componentWidth,
      colWidths,
      columnCount,
      pingedLeft,
      pingedRight,
      scrollXStyle,
      scrollYStyle,
      scrollTableStyle,
      scrollBodyRef,
      scrollHeaderRef,
      scrollSummaryRef,
      fullTableRef,
      stickyRef,
      scrollBodySizeInfo,
      summaryCollect,
      onColumnResize,
      onFullTableResize,
      onScroll
    } = useScroll(props, flattenColumns);
    // ====================== Scroll ======================

    // ==================== Providers =====================
    useProvideTable(
      reactive({
        ...toRefs(reactivePick(props, 'transformCellText')),
        getComponent,
        scrollbarSize,
        fixedInfoList: computed(() =>
          flattenColumns.value.map((_, colIndex) =>
            getCellFixedInfo(colIndex, colIndex, flattenColumns.value, stickyOffsets.value)
          )
        ),
        isSticky: computed(() => stickyState.value.isSticky),
        summaryCollect
      })
    );

    useProvideBody(
      reactive({
        ...toRefs(
          reactivePick(
            props,
            'rowClassName',
            'expandedRowClassName',
            'expandRowByClick',
            'expandedRowRender',
            'expandIconColumnIndex',
            'indentSize'
          )
        ),
        columns,
        flattenColumns,
        tableLayout: mergedTableLayout,
        expandIcon: mergedExpandIcon,
        expandableType,
        onTriggerExpand
      })
    );

    useProvideResize({
      onColumnResize
    });

    useProvideExpandedRow({
      componentWidth,
      fixHeader,
      fixColumn,
      horizonScroll
    });
    // ==================== Providers =====================

    // ====================== Render ======================
    const renderEmptyNode = () => {
      return hasData.value ? null : slots.emptyText?.() || 'No Data';
    };
    const renderBody = () =>
      h(
        Body,
        {
          data: mergedData.value,
          getRowKey: getRowKey.value,
          measureColumnWidth: fixHeader.value || horizonScroll.value || stickyState.value.isSticky,
          expandedKeys: mergedExpandedKeys.value,
          customRow: props.customRow,
          rowExpandable: props.rowExpandable,
          childrenColumnName: mergedChildrenColumnName.value
        },
        { emptyNode: () => renderEmptyNode() }
      );
    const renderBodyColGroup = () =>
      h(ColGroup, { colWidths: flattenColumns.value.map(({ width }) => width), columns: flattenColumns.value });

    useRender(() => {
      const {
        id,
        scroll,
        tableLayout,

        // Customize
        showHeader,
        customHeaderRow,

        // Additional
        header = slots.header,
        footer = slots.footer,
        toolbar = slots.toolbar
      } = props;

      const headerProps = {
        colWidths: colWidths.value,
        columnCount: flattenColumns.value.length,
        stickyOffsets: stickyOffsets.value,
        customHeaderRow,
        fixHeader: fixHeader.value,
        scroll
      };

      const { isSticky, offsetHeader, offsetSummary, offsetScroll, stickyClassName, container } = stickyState.value;

      const fixedHolderProps = {
        noData: !mergedData.value.length,
        maxContentScroll: horizonScroll.value && scroll?.x === 'max-content',
        ...headerProps,
        ...columnContext.value,
        stickyClassName,
        onScroll
      };

      const TableComponent = getComponent(['table'], 'table');
      const customizeScrollBody = getComponent(['body']);

      const tableLayoutClasses = computed(() => ({
        [`cds-table__table-layout--${unref(mergedTableLayout)}`]: !!unref(mergedTableLayout)
      }));

      const summaryNode = slots.summary?.({ pageData: mergedData.value });

      if (typeof customizeScrollBody === 'function' && hasData.value && !fixHeader.value) {
        warn('CdsTable', '`components.body` with render props is only work on `scroll.y`.');
      }

      let groupTableNode = (): VNode | null => null;

      if (fixHeader.value || isSticky) {
        // RENDER FIXED HEADER
        let bodyContent = (): VNode | null => null;

        if (typeof customizeScrollBody === 'function') {
          bodyContent = () =>
            customizeScrollBody(mergedData.value, { scrollbarSize: scrollbarSize.value, ref: scrollBodyRef, onScroll });

          headerProps.colWidths = flattenColumns.value.map(({ width }, index) => {
            const colWidth = index === columns.value.length - 1 ? (width as number) - scrollbarSize.value : width;

            if (typeof colWidth === 'number' && !Number.isNaN(colWidth)) {
              return colWidth;
            }

            warn(
              'CdsTable',
              'When use `components.body` with render props. Each column should have a fixed `width` value.'
            );

            return 0;
          }) as number[];
        } else {
          bodyContent = () =>
            h(
              'div',
              {
                ref: scrollBodyRef,
                class: 'cds-table__body',
                style: { ...scrollXStyle.value, ...scrollYStyle.value },
                onScroll
              },
              h(
                TableComponent,
                {
                  class: { ...unref(tableLayoutClasses) },
                  style: { ...scrollTableStyle.value, tableLayout: mergedTableLayout.value }
                },
                [
                  renderBodyColGroup(),
                  renderBody(),
                  !fixFooter.value &&
                    summaryNode &&
                    h(Footer, { stickyOffsets: stickyOffsets.value, flattenColumns: flattenColumns.value }, summaryNode)
                ]
              )
            );
        }

        groupTableNode = () =>
          h(Fragment, [
            // Header Table
            showHeader !== false &&
              h(
                FixedHolder,
                {
                  ...fixedHolderProps,
                  ref: scrollHeaderRef,
                  stickyTopOffset: offsetHeader,
                  class: 'cds-table__header'
                },
                {
                  default: (fixedHolderPassProps: IFixedHolderProps) =>
                    h(Fragment, [
                      h(Header, { ...fixedHolderPassProps }),
                      fixFooter.value === 'top' && h(Footer, { ...fixedHolderPassProps }, summaryNode)
                    ])
                }
              ),

            // Body Table
            bodyContent(),

            // Summary table
            fixFooter &&
              fixFooter.value !== 'top' &&
              h(
                FixedHolder,
                {
                  ...fixedHolderProps,
                  ref: scrollSummaryRef,
                  stickyBottomOffset: offsetSummary,
                  class: 'cds-table__summary'
                },
                {
                  default: (fixedHolderPassProps: IFixedHolderProps) =>
                    h(Footer, { ...fixedHolderPassProps }, summaryNode)
                }
              ),

            // Sticky scroll bar
            isSticky &&
              scrollBodyRef.value &&
              h(StickyScrollbar, {
                ref: stickyRef,
                scrollBodySizeInfo: scrollBodySizeInfo.value,
                offsetScroll,
                scrollBodyRef,
                container,
                onScroll
              })
          ]);
      } else {
        // RENDER UNIQUE TABLE
        groupTableNode = () =>
          h(
            'div',
            {
              ref: scrollBodyRef,
              class: 'cds-table__content',
              style: { ...scrollXStyle.value, ...scrollYStyle.value },
              onScroll
            },
            h(
              TableComponent,
              {
                class: { ...unref(tableLayoutClasses) },
                style: { ...scrollTableStyle.value, tableLayout: mergedTableLayout.value }
              },
              [
                renderBodyColGroup(),
                showHeader !== false && h(Header, { ...headerProps, ...columnContext.value }),
                renderBody(),
                summaryNode &&
                  h(Footer, { stickyOffsets: stickyOffsets.value, flattenColumns: flattenColumns.value }, summaryNode)
              ]
            )
          );
      }

      const ariaProps = pickAttrs(attrs, { aria: true, data: true });
      const fullTable = () =>
        h(
          'div',
          {
            ...ariaProps,
            id,
            ref: fullTableRef,
            class: classNames({
              'cds-table': true,
              'cds-table--layout-fixed': tableLayout === 'fixed',
              'cds-table--fixed-header': fixHeader.value,
              'cds-table--fixed-column': fixColumn.value,
              'cds-table--ping-left': pingedLeft.value,
              'cds-table--ping-right': pingedRight.value,
              'cds-table--scroll-horizontal': horizonScroll.value,
              'cds-table--has-fix-left': flattenColumns.value[0] && flattenColumns.value[0].fixed,
              'cds-table--has-fix-right':
                flattenColumns.value[columnCount.value - 1] &&
                flattenColumns.value[columnCount.value - 1].fixed === 'right',
              [attrs.class as string]: attrs.class
            }),
            style: attrs.style as CSSProperties
          },
          [
            header && h(Panel, { class: 'cds-table__title' }, { default: () => header(unref(mergedData)) }),
            toolbar && h(Panel, { class: 'cds-table__toolbar' }, { default: () => toolbar(unref(mergedData)) }),
            h('div', { class: 'cds-table__container' }, groupTableNode() as VNode),
            footer && h(Panel, { class: 'cds-table__footer' }, { default: () => footer(unref(mergedData)) })
          ]
        );

      if (horizonScroll.value) {
        return h(
          ResizeObserver,
          { onResize: onFullTableResize },
          {
            default: () => fullTable()
          }
        );
      }

      return fullTable();
    });
    // ====================== Render ======================
  }
});
